import { until } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { createTicket } from '~/helpers/api/tickets';
import { useStore } from 'vuex';

type ChatInstance = ReturnType<typeof useChat>;
type BubbleState = 'closed' | 'ai-chat' | 'inbox';

const globalChatInstance = ref<ChatInstance | null>(null);
export const setGlobalChatInstance = (chatInstanceParam: ChatInstance) => {
  globalChatInstance.value = chatInstanceParam;
};

export const useAiChatWidget = () => {
  // Use useState for serializable shared state
  const bubbleState = useState<BubbleState>('aiChatWidget.bubbleState', () => 'closed');
  const loadDrawer = useState<boolean>('aiChatWidget.loadDrawer', () => false);

  const recaptcha = useReCaptcha();
  const { executeRecaptcha, recaptchaLoaded } = recaptcha || {};

  // Handles internal lazy loading of the drawer, so that the drawer is not loaded until the user hovers over the bubble.
  const handleFirstHover = () => {
    loadDrawer.value = true;
  };

  const store = useStore();
  const aiSettings = computed(() => store.getters.getCmsAiSettings);
  const aiChatEnabled = computed(() => aiSettings.value.ai_chat_enabled);
  const aiContactFormEnabled = computed(() => aiSettings.value.tasks_enabled);
  const aiSearchEnabled = computed(() => aiSettings.value.ai_search_enabled);

  const handleStateChange = (state: BubbleState) => {
    bubbleState.value = state;
  };

  const handleDrawerClose = () => {
    bubbleState.value = 'closed';
  };

  const isOpen = computed(() => {
    return bubbleState.value !== 'closed';
  });

  const handleSubmit = async (formData: any) => {
    try {
      const { $store } = useNuxtApp();
      const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;

      const parentOrgId = ($store as any).state.parentOrgId;
      const secondaryOrgId = ($store as any).state.secondaryOrgId;

      if (!writableApi) {
        throw new Error('API configuration missing');
      }

      if (!parentOrgId) {
        throw new Error('Organization ID is required');
      }

      if (!formData.email) {
        throw new Error('Email is required');
      }

      if (!formData.name) {
        throw new Error('Name is required');
      }

      let recaptchaToken = '';
      if (recaptchaLoaded && executeRecaptcha) {
        await recaptchaLoaded();
        recaptchaToken = await executeRecaptcha('ai_chat_ticket');
      }

      const ticketData = {
        subject: `Support Request - ${formData.topic?.category || 'General Inquiry'}`,
        description: formData.description || 'No description provided',
        parent_organization_id: parentOrgId,
        secondary_organization_id: formData.school || secondaryOrgId,
        requester: {
          email: formData.email,
          name: formData.name,
        },
        topic_id: formData.topic,
        chat_session_id: formData.chatSessionId,
      };

      const result = await createTicket({
        endpoint: writableApi,
        ticketData,
        recaptcha_token: recaptchaToken,
      });

      return result;
    } catch (error) {
      console.error('Failed to create ticket:', error);
      throw error;
    }
  };
  const openAiChat = () => {
    if (!loadDrawer.value) {
      loadDrawer.value = true;
    }
    handleStateChange('ai-chat');
  };

  const sendMessage = async (message: string, customData: Record<string, unknown> = {}) => {
    if (!globalChatInstance.value) {
      console.error('Chat instance not initialized');
      return false;
    }

    try {
      await until(() => globalChatInstance.value?.isConnected).toBe(true, { timeout: 5000, throwOnTimeout: true });
      await globalChatInstance.value.sendMessage(message, customData);
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  };

  return {
    bubbleState,
    aiChatEnabled,
    aiContactFormEnabled,
    aiSearchEnabled,
    isOpen,
    loadDrawer,
    handleStateChange,
    handleDrawerClose,
    handleFirstHover,
    handleSubmit,
    chat: readonly(globalChatInstance),
    openAiChat,
    sendMessage,
  };
};
